import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Users, Calendar, Clock, Trophy } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

interface TournamentWithPlayers extends Tables<"tournaments"> {
  tournament_players: { id: string }[];
}

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  registration: "bg-green-500/15 text-green-700",
  active: "bg-blue-500/15 text-blue-700",
  completed: "bg-gray-500/15 text-gray-700",
  cancelled: "bg-red-500/15 text-red-700",
};

export default function Tournaments({ embedded = false }: { embedded?: boolean }) {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const { t } = useTranslation("activities");

  const formatLabels: Record<string, string> = {
    americano: "Americano",
    mexicano: "Mexicano",
    team_mexicano: "Team Mexicano",
    knockout: t("tournament.format.knockout"),
    round_robin: "Round Robin",
  };

  const statusLabels: Record<string, string> = {
    draft: t("common:status.draft"),
    registration: t("tournament.status.registration"),
    active: t("tournament.status.active"),
    completed: t("tournament.status.completed"),
    cancelled: t("tournament.status.cancelled"),
  };

  const priceSuffix = (amount: number) => t("common:currencySuffix", { amount });

  const { data: tournaments = [], isLoading } = useQuery({
    queryKey: ["tournaments", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournaments")
        .select("*, tournament_players(id)")
        .eq("organization_id", organization!.id)
        .neq("status", "cancelled")
        .order("date", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization,
  });

  const upcoming = tournaments.filter((t: TournamentWithPlayers) => ["draft", "registration", "active"].includes(t.status));
  const past = tournaments.filter((t: TournamentWithPlayers) => t.status === "completed");

  const TournamentCard = ({ t }: { t: TournamentWithPlayers }) => (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/tournaments/${t.id}`)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm truncate">{t.name}</h3>
              <Badge className={statusColors[t.status]} variant="secondary">{statusLabels[t.status]}</Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {format(parseISO(t.date), "d MMM", { locale: sv })}</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.start_time?.slice(0, 5)}</span>
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {t.tournament_players?.length ?? 0}/{t.max_players}</span>
            </div>
            <Badge variant="outline" className="text-xs">{formatLabels[t.format]}</Badge>
            {(t.price_per_player ?? 0) > 0 && (
              <span className="text-xs text-muted-foreground ml-2">{priceSuffix(t.price_per_player)}</span>
            )}
          </div>
          <Trophy className="h-8 w-8 text-muted-foreground/30 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );

  // Early returns AFTER TournamentCard declaration so the embedded
  // upcoming.map(...) does not hit a TDZ on TournamentCard (regression
  // surfaced by PR #274 when the demo Tournaments tab rendered with
  // upcoming.length > 0 for the first time).
  if (isLoading && !embedded) {
    return (
      <div className="space-y-4 pb-20">
        <h1 className="text-2xl font-bold">{t("common:nav.tournaments")}</h1>
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  if (embedded) {
    return upcoming.length > 0 ? (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("common:nav.tournaments")}</h3>
        {upcoming.map((t: TournamentWithPlayers) => <TournamentCard key={t.id} t={t} />)}
      </div>
    ) : null;
  }

  return (
    <div className="space-y-4 pb-20">
      <div className="gradient-hero rounded-2xl p-5 -mx-1">
        <h1 className="text-2xl font-bold">{t("common:nav.tournaments")}</h1>
        <p className="text-sm text-muted-foreground">{t("tournament.subtitle")}</p>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList className="w-full">
          <TabsTrigger value="upcoming" className="flex-1">{t("common:upcoming")}</TabsTrigger>
          <TabsTrigger value="past" className="flex-1">{t("tournament.tabs.past")}</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-3 mt-3">
          {upcoming.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">{t("tournament.noUpcoming")}</p>
          ) : (
            upcoming.map((t: TournamentWithPlayers) => <TournamentCard key={t.id} t={t} />)
          )}
        </TabsContent>

        <TabsContent value="past" className="space-y-3 mt-3">
          {past.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">{t("tournament.noPast")}</p>
          ) : (
            past.map((t: TournamentWithPlayers) => <TournamentCard key={t.id} t={t} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
